import React, { useState, useEffect } from "react";
import {
  Send,
  X,
  Home,
  HelpCircle,
  MessageSquare,
  ChevronRight,
  ArrowLeft,
  Building2,
  Users,
  LayoutGrid,
} from "lucide-react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "./css/chatbottwo.css";

// FAQ_QUESTIONS and HELP_SECTIONS remain unchanged
const FAQ_QUESTIONS = [
  {
    question: "What is CLIF AI?",
    answer:
      "CLIF AI is a career growth platform powered by Artificial Intelligence. It helps students and professionals discover the right career paths, develop essential skills, and stay on track. With personalized learning and expert support, CLIF makes career planning simple and smart.",
    image: "/images/banner/Front.jpg",
  },
  {
    question: "How can CLIF help me?",
    answer:
      "CLIF analyzes your goals, interests, and skills to guide you on the best career path. It offers tailored learning plans, mentorship, and regular progress tracking. Whether you're just starting out or looking to grow, CLIF supports you every step of the way.",
    image: "/images/recruiters/group-6.jpg",
  },
  {
    question: "Is CLIF free to use?",
    answer:
      "Yes, CLIF offers a free plan with access to essential features for a limited period of time. You can explore learning content, get career suggestions, and more. For deeper insights, mentorship, and premium tools, you can upgrade to a paid plan anytime.",
    image: "/images/recruiters/group-10.jpg",
  },
];

const HELP_SECTIONS = [
  {
    title: "Getting Started",
    content:
      "Welcome to CLIF – your personal career growth companion! To get started, simply sign up and tell us about your background, interests, and goals. Based on your profile, CLIF builds a personalized learning and career roadmap just for you. You’ll get skill-building tasks, mentor suggestions, and job-ready action plans. The dashboard is super easy to use. Start exploring and let Cliffy guide you every step of the way! [Sign up now!](https://clif.ai/signup)",
    image: "/images/recruiters/group-9.jpg",
    icon: Home,
  },
  {
    title: "How CLIF Works",
    content:
      "CLIF uses powerful AI combined with expert insights to help you plan your career. Once you join, it analyzes your strengths, interests, and goals to create a custom learning path. You'll receive course recommendations, mentorship options, and smart nudges to stay on track. Our platform adjusts as you grow, making sure your plan evolves with you. Whether you're a student or pro, CLIF keeps your career game strong. [Learn more!](https://clif.ai/dream-career)",
    image: "/images/recruiters/group-7.jpg",
    icon: LayoutGrid,
  },
  {
    title: "How CLIF Tracks Your Progress",
    content:
      "CLIF tracks your learning and career progress automatically through smart dashboards. As you complete tasks, attend sessions, and hit milestones, your profile gets updated in real-time. You’ll see progress reports, skill badges, and personalized suggestions. Weekly updates and reminders help you stay on track. It’s your personal roadmap to success — always clear and motivating. [Learn more!](https://clif.ai/about-us)",
    image: "/images/team/thumb-1.jpg",
    icon: Users,
  },
  {
    title: "Top Career Matches & Job Opportunities",
    content:
      "CLIF helps you explore careers that match your interests, skills, and goals. We show you job roles, industries, and opportunities that fit your profile. You also get alerts about internships and openings from our partner companies and recruiters. Cliffy ensures you're not just learning, but actually moving closer to the job you want. It’s your smart career matchmaker!",
    image: "/images/recruiters/group-5.jpg",
    icon: Building2,
  },
];

const HelpSection = ({ selectedHelp, setSelectedHelp }) => {
  if (selectedHelp) {
    return (
      <div className="help-detail-container">
        <div className="help-detail-header">
          <button onClick={() => setSelectedHelp(null)} className="back-button">
            <ArrowLeft size={16} className="me-2" />
            Back
          </button>
        </div>
        <div className="help-detail-content">
          <div className="container">
            <h2 className="help-title">{selectedHelp.title}</h2>
            <div className="row">
              <div className="col-md-6">
                <p
                  className="help-text"
                  dangerouslySetInnerHTML={{
                    __html: selectedHelp.content.replace(
                      /\[(.*?)\]\((.*?)\)/g,
                      '<a href="$2" target="_blank">$1</a>'
                    ),
                  }}
                />
              </div>
              <div className="col-md-6">
                <div className="help-image-container">
                  <img
                    src={selectedHelp.image}
                    alt={selectedHelp.title}
                    className="help-image"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="help-section-container">
      <div className="help-list">
        {HELP_SECTIONS.map((section, index) => {
          const Icon = section.icon;
          return (
            <button
              key={index}
              onClick={() => setSelectedHelp(section)}
              className="help-card">
              <div className="help-card-content">
                <div className="help-icon-container">
                  <Icon size={18} className="help-icon" />
                </div>
                <span className="help-card-title">{section.title}</span>
              </div>
              <ChevronRight size={16} className="help-card-arrow" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

function ChatbotTwo() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [selectedFAQ, setSelectedFAQ] = useState(null);
  const [selectedHelp, setSelectedHelp] = useState(null);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [chatStartTime, setChatStartTime] = useState(null);
  const [userName, setUserName] = useState(null); // Store user's name
  const [waitingForName, setWaitingForName] = useState(false); // Track if waiting for name
  const messagesEndRef = React.useRef(null);

  const welcomeMessages = [
    { sender: "bot", text: "Cliffy in the house!" },
    { sender: "bot", text: "Let’s level up your career game." },
    {
      sender: "bot",
      text: "Okay, before the magic begins, tell me your name first!",
    },
  ];

  // Function to validate if input resembles a name
  const isValidName = (input) => {
    // Simple heuristic: allows letters, spaces, hyphens, and apostrophes; max 50 chars
    const nameRegex = /^[a-zA-Z\s\-']{1,50}$/;
    // Also check if input contains common question words
    const questionWords = /\b(how|what|where|when|why|is|are|can|do|does)\b/i;
    return nameRegex.test(input) && !questionWords.test(input);
  };

  // Updated system prompt to use the user's name or a default
  const systemPrompt = (name) => `
  You are Cliffy, a smart, friendly, and to-the-point AI chatbot for CLIF AI — a global tech platform founded in 2025 and headquartered in Princeton, New Jersey. The company was created by tech-savvy professionals from the U.S. and offers an advanced AI-human hybrid system to guide users toward their dream careers.

   If a user's name is provided (${
     name || "unknown"
   }), address the user by their name in every response. If no name is provided, do not address the user by any name or placeholder (e.g., do not use "User"); simply provide the response without a greeting or address.

  Your job is to help users understand and navigate CLIF AI’s services, features, and current offerings. You should always sound confident, professional, and concise — keep every response limited to 2–3 lines max, clear, and bot-like. Do not use emojis or casual phrases. You are helpful, never vague.

  You can answer questions related to:
  • Features of CLIF AI (resume builder, personalized career roadmap, video interview analysis, skill tracking, etc.)
  • Target audiences: Individuals (students/professionals), university representatives, and recruiters
  • Use cases: Upskilling, mentorship, job preparation, hiring & training
  • Global availability, platform usage, and pricing model
  • Technical support or bug-related issues

  Key Features to Highlight:
  • AI-powered personalized career roadmap
  • 1:1 mentorship and guidance from industry experts
  • Resume building and enhancement tools
  • Video interview analysis with confidence, pitch, and eye contact scoring
  • Skill tracking, both core and professional
  • Training modules for in-demand technical and soft skills
  • A single platform for individuals, universities, and recruiters

  Additional Instructions:
  • If the user interacts for more than 5–6 minutes or has a complaint, politely ask them to email: connect@clif.ai
  • Only if you are unsure, refer the user to: clif.ai
  • Never generate long explanations or casual conversations

  You are the face of the best AI career platform — confident, helpful, to-the-point.
`;

  const renderMessages = () => (
    <div className="chat-container">
      <div className="message-list">
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`message ${
              msg.sender === "user" ? "message-user" : "message-bot"
            }`}>
            {msg.sender === "bot" && (
              <div className="message-avatar">
                <img
                  src="../../images/chatbot/avatar.png"
                  alt="Bot Avatar"
                  className="message-avatar-img"
                />
              </div>
            )}
            <div
              className={`message-content ${
                msg.sender === "user"
                  ? "message-content-user"
                  : "message-content-bot"
              }`}>
              <p className="message-text">{msg.text}</p>
              <span className="message-time">
                {new Date().toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="message message-bot">
            <div className="message-avatar">
              <img
                src="../../images/chatbot/avatar.png"
                alt="Bot Avatar"
                className="message-avatar-img"
              />
            </div>
            <div className="typing-indicator">
              <span className="typing-dot"></span>
              <span className="typing-dot"></span>
              <span className="typing-dot"></span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="chat-input-container">
        <form onSubmit={sendMessage} className="chat-input-form">
          <input
            type="text"
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="chat-input"
          />
          <button type="submit" className="chat-send-button">
            <Send size={16} />
          </button>
        </form>
      </div>
    </div>
  );

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim()) return;

    const userMessage = input;
    setMessages((prev) => [...prev, { sender: "user", text: userMessage }]);
    setInput("");
    setIsTyping(true);

    // Set chat start time if this is the first message
    if (!chatStartTime) setChatStartTime(Date.now());

    try {
      // Handle name input
      if (waitingForName && messages.length === welcomeMessages.length) {
        if (isValidName(userMessage)) {
          setUserName(userMessage); // Store valid name
          setWaitingForName(false); // Stop waiting for name
          setMessages((prev) => [
            ...prev,
            {
              sender: "bot",
              text: `Nice to meet you, ${userMessage}! How can I assist you today?`,
            },
          ]);
        } else {
          // Treat invalid input as a question, don't set name
          setWaitingForName(false); // Stop waiting for name
          // Process the question directly
          const response = await axios.post(
            process.env.REACT_APP_OPENAI_API_URL,
            {
              model: "gpt-3.5-turbo",
              messages: [
                { role: "system", content: systemPrompt(null) },
                { role: "user", content: userMessage },
              ],
            },
            {
              headers: {
                Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
                "Content-Type": "application/json",
              },
            }
          );

          const botResponse =
            response.data.choices?.[0]?.message?.content ||
            "Error generating response.";
          setMessages((prev) => [
            ...prev,
            { sender: "bot", text: botResponse },
          ]);
        }
        setIsTyping(false);
        return;
      }

      // Normal message processing
      const response = await axios.post(
        process.env.REACT_APP_OPENAI_API_URL,
        {
          model: "gpt-3.5-turbo",
          messages: [
            { role: "system", content: systemPrompt(userName) },
            { role: "user", content: userMessage },
          ],
        },
        {
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
            "Content-Type": "application/json",
          },
        }
      );

      const botResponse =
        response.data.choices?.[0]?.message?.content ||
        "Error generating response.";
      setMessages((prev) => [...prev, { sender: "bot", text: botResponse }]);

      // Check chat duration (5-6 minutes = 300,000–360,000 ms)
      const chatDuration = Date.now() - chatStartTime;
      if (
        chatDuration > 300000 ||
        botResponse.toLowerCase().includes("complain")
      ) {
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: "For further assistance, please write to connect@clif.ai.",
          },
        ]);
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: "Sorry, I encountered an error. Please try again.",
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const addMessagesSequentially = async () => {
    for (let i = 0; i < welcomeMessages.length; i++) {
      setIsTyping(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setMessages((prev) => [...prev, welcomeMessages[i]]);
      setIsTyping(false);
      if (i < welcomeMessages.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
    setWaitingForName(true); // Set flag to wait for the user's name
  };

  useEffect(() => {
    if (isOpen && activeSection === "messages" && messages.length === 0) {
      addMessagesSequentially();
    }
  }, [isOpen, activeSection]);

  const renderHome = () => (
    <div className="home-container">
      <div className="home-header">
        <div className="home-header-text">
          <h1 className="home-title">Let’s Plan Your Career</h1>
          <h2 className="home-subtitle">
            Hi, I’m Cliffy – here to guide you to your dream career – ask me
            anything!👋
          </h2>
        </div>
        <div className="home-avatar">
          <img
            src="../../images/chatbot/avatar.png"
            alt="Cliffy Avatar"
            className="home-avatar-img"
          />
        </div>
      </div>
      <div className="home-content">
        <div className="faq-container">
          {FAQ_QUESTIONS.map((faq, index) => (
            <button
              key={index}
              onClick={() => setSelectedFAQ(faq)}
              className="faq-card">
              <span className="faq-question">{faq.question}</span>
              <ChevronRight size={16} className="faq-arrow" />
            </button>
          ))}
        </div>
        <button
          onClick={() => setActiveSection("messages")}
          className="contact-button">
          <div className="contact-title">Send us a message</div>
          <div className="contact-subtitle">
            We will reply as soon as we can
          </div>
        </button>
      </div>
    </div>
  );

  const renderFAQDetail = () => (
    <div className="faq-detail-container">
      <div className="faq-detail-header">
        <button onClick={() => setSelectedFAQ(null)} className="back-button">
          <ArrowLeft size={16} className="me-2" />
          Back
        </button>
      </div>
      <div className="faq-detail-content">
        <div className="container">
          <h2 className="faq-title">{selectedFAQ.question}</h2>
          <div className="row">
            <div className="col-md-6">
              <p className="faq-answer">{selectedFAQ.answer}</p>
            </div>
            <div className="col-md-6">
              <div className="faq-image-container">
                <img
                  src={selectedFAQ.image}
                  alt={selectedFAQ.question}
                  className="faq-image"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const getChatbotDimensions = () => {
    if (window.innerWidth <= 640) {
      return {
        width: "100vw",
        height: "100vh",
        position: "fixed",
        top: "0",
        bottom: "0",
        left: "0",
        right: "0",
        zIndex: "9999",
      };
    } else if (window.innerWidth <= 1024) {
      return selectedFAQ || selectedHelp
        ? { width: "600px", height: "500px" }
        : { width: "350px", height: "500px" };
    } else {
      return selectedFAQ || selectedHelp
        ? { width: "800px", height: "600px" }
        : { width: "400px", height: "600px" };
    }
  };

  return (
    <div className="chatbot-container">
      {!isOpen ? (
        <button
          onClick={() => setIsOpen(true)}
          className="chatbot-toggle-button">
          <img
            src="../../images/chatbot/avatar.png"
            alt="Bot Avatar"
            className="message-avatar-img"
          />
        </button>
      ) : (
        <div className="chatbot-window" style={getChatbotDimensions()}>
          <div className="chatbot-header">
            <div className="chatbot-header-content">
              <div className="chatbot-logo">
                <img
                  src="../../images/logo.png"
                  alt="Logo"
                  className="logo-img"
                />
              </div>
              <button onClick={() => setIsOpen(false)} className="close-button">
                <X size={18} />
              </button>
            </div>
          </div>
          <div className="chatbot-body">
            {selectedFAQ ? (
              renderFAQDetail()
            ) : (
              <>
                {activeSection === "home" && renderHome()}
                {activeSection === "messages" && renderMessages()}
                {activeSection === "help" && (
                  <HelpSection
                    selectedHelp={selectedHelp}
                    setSelectedHelp={setSelectedHelp}
                  />
                )}
              </>
            )}
          </div>
          <div className="chatbot-footer">
            <div className="chatbot-nav">
              {[
                { section: "home", icon: Home, label: "Home" },
                { section: "messages", icon: MessageSquare, label: "Messages" },
                { section: "help", icon: HelpCircle, label: "Help" },
              ].map(({ section, icon: Icon, label }) => (
                <button
                  key={section}
                  onClick={() => {
                    setActiveSection(section);
                    setSelectedFAQ(null);
                  }}
                  className={`nav-button ${
                    activeSection === section ? "nav-button-active" : ""
                  }`}>
                  <Icon size={18} className="nav-icon" />
                  <span className="nav-label">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatbotTwo;
